/* global process */
import axios from 'axios';
import { getValidIdToken } from "../services/authService";
import { getGlobalSessionPopupTrigger } from '../services/authService';
import logger from '../utils/logger.js';

// Detect GraphQL HTTP requests so we can force-refresh the token before each GraphQL call.
// Requirement: token API should be triggered before every GraphQL API (HTTP) call.
// Websocket/subscriptions are not made via axios and are therefore unaffected.
const isGraphQLHttpRequest = (config) => {
    if (!config) return false;

    const method = String(config.method || '').toLowerCase();
    if (method !== 'post') return false;

    const data = config.data;
    if (!data) return false;

    const hasQueryField = (payload) => {
        if (!payload || typeof payload !== 'object') return false;
        return typeof payload.query === 'string' && payload.query.trim().length > 0;
    };

    // Most of our calls send `JSON.stringify({ query })`.
    if (typeof data === 'string') {
        try {
            return hasQueryField(JSON.parse(data));
        } catch {
            return false;
        }
    }

    // Defensive: if callers ever send an object directly.
    if (typeof data === 'object') {
        return hasQueryField(data);
    }

    return false;
};

const axiosInstance = axios.create({
    baseURL: process.env.BASE_URL,

    headers: {
        'Content-Type': 'application/json',
    }
});

axiosInstance.interceptors.request.use(
    async config => {
        const forceRefresh = isGraphQLHttpRequest(config);
        const token = await getValidIdToken(forceRefresh);
        if (!token) {
            const authError = new Error('Auth token unavailable for request.');
            authError.code = 'AUTH_TOKEN_UNAVAILABLE';
            authError.isTokenUnavailable = true;
            return Promise.reject(authError);
        }

        config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    error => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error?.code === 'AUTH_TOKEN_UNAVAILABLE' || error?.isTokenUnavailable) {
            return Promise.reject(error);
        }

        const status = error?.response?.status;
        logger.warn("🔁 Axios response error:", {
            url: error?.config?.url,
            status: error?.response?.status,
            data: error?.response?.data,
        });
        if (status === 401) {
            sessionStorage.clear();
            const popupFn = getGlobalSessionPopupTrigger();
            if (popupFn) popupFn();
        }
        return Promise.reject(error);
    }
)
export default axiosInstance;